import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiGet, apiPost, getApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Card } from '../../components/Card';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { PageScaffold } from '../../components/PageScaffold';
import { StatusPill, orderStatusTone } from '../../components/StatusPill';
import { EmptyState } from '../../components/EmptyState';
import { Modal } from '../../components/Modal';
import { Pagination } from '../../components/Pagination';
import { SearchSelect } from '../../components/SearchSelect';
import { Can, useHasPermission, useHasAnyPermission } from '../../lib/permissions';
import { formatMUR } from '../../lib/money';
import { formatBusinessDate } from '../../lib/dates';

const PAGE_SIZE = 25;

interface StockLocation {
  id: string;
  name: string;
  kind: 'warehouse' | 'shop';
  shop: { id: string; code: string; name: string } | null;
}

interface StockLevel {
  id: string;
  onHand: number;
  reserved: number;
  display: number;
  minThreshold: number;
  product: { id: string; sku: string; name: string };
  location: { id: string; name: string; kind: 'warehouse' | 'shop'; shopId: string | null };
}

// A showroom floor piece (see backend display.service.ts). Open units
// (ON_DISPLAY / FOR_SALE) count toward StockLevel.display.
interface DisplayUnit {
  id: string;
  status: 'ON_DISPLAY' | 'FOR_SALE' | 'SOLD' | 'RETURNED_TO_STOCK' | 'WRITTEN_OFF';
  condition: string | null;
  clearancePrice: string | null;
  assignedAt: string;
  closedAt: string | null;
  note: string | null;
  product: {
    id: string;
    sku: string;
    name: string;
    size: string | null;
    basePrice: string;
    salePrice: string | null;
    category: { id: string; name: string; fulfillmentStrategy: string };
  };
  location: { id: string; name: string; kind: 'warehouse' | 'shop'; shopId: string | null };
  assignedBy: { id: string; name: string };
  closedBy: { id: string; name: string } | null;
}

interface Product {
  id: string;
  sku: string;
  name: string;
}

export function StockPage(): JSX.Element {
  const { user, perms } = useAuth();
  // A shop-floor user is scoped to one location; a cross-shop user (owner/admin/
  // operations) sees every location + the receive-stock controls. Operations are
  // now homed at HQ but hold orders.view_all_shops, so exclude them; a home-less
  // viewer keeps the all-locations read-only view.
  const isShopUser = !!user?.homeShopId && !perms.has('orders.view_all_shops');
  // The shop she's currently working from, for the "showing X only" labels — the
  // server scopes stock to this same active shop.
  const currentShopName = user?.activeShop?.name ?? user?.homeShop?.name ?? 'your shop';
  const [locationId, setLocationId] = useState<string | 'all'>('all');
  const [lowOnly, setLowOnly] = useState(false);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [reservedFor, setReservedFor] = useState<StockLevel | null>(null);
  const [displayFor, setDisplayFor] = useState<StockLevel | null>(null);

  const locations = useQuery({ queryKey: ['stock-locations'], queryFn: () => apiGet<StockLocation[]>('/stock/locations') });
  const levels = useQuery({
    queryKey: ['stock-levels', { locationId, lowOnly }],
    queryFn: () =>
      apiGet<StockLevel[]>('/stock/levels', {
        ...(locationId !== 'all' ? { locationId } : {}),
        ...(lowOnly ? { lowOnly: 'true' } : {}),
      }),
  });

  // Client-side search (product name / SKU / location) over the fetched set,
  // then paginate. Mirrors OrdersListPage — the endpoint returns the whole list.
  const filtered = useMemo(() => {
    const list = levels.data ?? [];
    const ql = q.trim().toLowerCase();
    if (!ql) return list;
    return list.filter((l) =>
      `${l.product.name} ${l.product.sku} ${l.location.name}`.toLowerCase().includes(ql),
    );
  }, [levels.data, q]);

  useEffect(() => { setPage(1); }, [q, locationId, lowOnly]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const canReceive = useHasPermission('stock.receive');
  const canTransfer = useHasPermission('stock.transfer');
  const canManageDisplay = useHasPermission('stock.display.manage');
  // The "Receive stock at HQ" action is for HQ ops only — a shop user never
  // receives stock from a supplier, she only confirms transfers from HQ.
  const showReceiveStock = canReceive && !isShopUser;

  // Export the current view as a branded inventory PDF. Mirrors the location
  // filter: a specific location exports just that location; "All locations"
  // exports everything with a Location column. (Shop users are scoped server-
  // side to their own shop, so the locationId param is only sent for HQ users.)
  const [exporting, setExporting] = useState(false);
  const exportPdf = (): void => {
    void (async () => {
      setExporting(true);
      try {
        const params: Record<string, string> = {};
        if (!isShopUser && locationId !== 'all') params.locationId = locationId;
        const res = await api.get('/stock/inventory-report.pdf', { params, responseType: 'blob' });
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const scope = isShopUser ? 'my-shop' : locationId === 'all' ? 'all-locations' : 'location';
        const a = document.createElement('a');
        a.href = url;
        a.download = `inventory-${scope}-${new Date().toISOString().slice(0, 10)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
      } catch (e) {
        console.error(e);
      } finally {
        setExporting(false);
      }
    })();
  };

  return (
    <PageScaffold
      title="Stock"
      subtitle={
        isShopUser
          ? `Live levels at ${currentShopName}. Confirm incoming transfers from HQ below.`
          : 'Per-location levels with low-stock alerts.'
      }
      actions={
        <div className="flex gap-2">
          <Button variant="secondary" onClick={exportPdf} loading={exporting}>
            Export PDF
          </Button>
          {/* Numbered pieces (imported mattresses) — see UnitNumbersPage. */}
          <Link to="/stock/units">
            <Button variant="secondary">Unit numbers</Button>
          </Link>
          {showReceiveStock && <ReceiveStockButton />}
          {canManageDisplay && <AddDisplayUnitButton />}
          {canTransfer && <TransferStockButton />}
        </div>
      }
    >

      {/* Incoming transfers — sales girl confirms goods arrived from HQ */}
      {isShopUser && <IncomingTransfersCard />}

      <Card bodyClassName="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            className="kc-input w-full sm:w-72"
            placeholder="Search product or SKU…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {isShopUser ? (
            <span className="text-sm text-ink-3">
              Showing{' '}
              <span className="text-ink font-medium">{currentShopName}</span>{' '}
              only
            </span>
          ) : (
            <>
              <label className="text-sm text-ink-3">Location</label>
              <SearchSelect
                className="w-56"
                value={locationId}
                onChange={(v) => setLocationId(v as string | 'all')}
                placeholder="Search a location…"
                options={[
                  { value: 'all', label: 'All locations' },
                  ...(locations.data ?? []).map((l) => ({ value: l.id, label: l.name })),
                ]}
              />
            </>
          )}
          <label className="flex items-center gap-2 text-sm text-ink-2 cursor-pointer ml-auto">
            <input type="checkbox" checked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} className="accent-accent" />
            Low stock only
          </label>
        </div>
      </Card>

      <Card bodyClassName="p-0">
        {levels.isLoading ? (
          <div className="text-center text-ink-3 px-6 py-12">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No stock records"
            description={q.trim() ? 'No product matches your search.' : 'Try a different filter.'}
          />
        ) : (
          <>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                <th className="px-6 py-2 font-medium">Product</th>
                {!isShopUser && <th className="px-6 py-2 font-medium">Location</th>}
                <th className="px-6 py-2 font-medium text-right">On hand</th>
                <th className="px-6 py-2 font-medium text-right">Reserved</th>
                <th className="px-6 py-2 font-medium text-right">Display</th>
                <th className="px-6 py-2 font-medium text-right">Available</th>
                <th className="px-6 py-2 font-medium text-right">Threshold</th>
                <th className="px-6 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {paged.map((l) => {
                const low = l.onHand - l.display <= l.minThreshold;
                const available = l.onHand - l.reserved - l.display;
                return (
                  <tr key={l.id} className="border-b border-rule-2 hover:bg-paper-2">
                    <td className="px-6 py-3">
                      <div className="text-ink">{l.product.name}</div>
                      <div className="text-xs text-ink-3 kc-mono">{l.product.sku}</div>
                    </td>
                    {!isShopUser && <td className="px-6 py-3 text-ink-2">{l.location.name}</td>}
                    <td className="px-6 py-3 text-right kc-mono kc-tnum">{l.onHand}</td>
                    <td className="px-6 py-3 text-right kc-mono kc-tnum">
                      {l.reserved !== 0 ? (
                        <button
                          type="button"
                          onClick={() => setReservedFor(l)}
                          className="text-accent hover:underline underline-offset-2"
                          title="See the orders holding this stock"
                        >
                          {l.reserved}
                        </button>
                      ) : (
                        <span className="text-ink-3">{l.reserved}</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-right kc-mono kc-tnum">
                      {l.display !== 0 ? (
                        <button
                          type="button"
                          onClick={() => setDisplayFor(l)}
                          className="text-accent hover:underline underline-offset-2"
                          title="See the showroom pieces behind this figure"
                        >
                          {l.display}
                        </button>
                      ) : (
                        <span className="text-ink-3">{l.display}</span>
                      )}
                    </td>
                    <td className={`px-6 py-3 text-right kc-mono kc-tnum font-medium ${available <= 0 ? 'text-negative' : 'text-ink'}`}>{available}</td>
                    <td className="px-6 py-3 text-right kc-mono kc-tnum text-ink-3">{l.minThreshold}</td>
                    <td className="px-6 py-3">{low && <StatusPill tone="warning">Low</StatusPill>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination
            page={safePage}
            pageCount={pageCount}
            total={filtered.length}
            pageSize={PAGE_SIZE}
            onChange={setPage}
            unit="item"
          />
          </>
        )}
      </Card>

      <Can permission="stock.transfer">
        <TransfersList />
      </Can>

      {reservedFor && <ReservedOrdersModal level={reservedFor} onClose={() => setReservedFor(null)} />}
      {displayFor && <DisplayUnitsModal level={displayFor} onClose={() => setDisplayFor(null)} />}
    </PageScaffold>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ReservedOrdersModal — click a Reserved figure to see the open orders holding
// it: backorder lines waiting on incoming stock, and produced made-to-order
// units sitting in the warehouse until their delivery. If the counter and the
// linked rows disagree (historical/manual adjustments), the gap is flagged.
// ─────────────────────────────────────────────────────────────────────────────

interface ReservedOrderRow {
  source: 'BACKORDER' | 'PRODUCED';
  qty: number;
  heldSince: string | null;
  order: {
    id: string;
    number: string | null;
    status: string;
    deliveryDate: string | null;
    customer: { type: string; name: string; companyName: string | null };
    shop: { code: string };
  };
}

function shortDate(iso: string | null): string {
  return formatBusinessDate(iso);
}

function ReservedOrdersModal({ level, onClose }: { level: StockLevel; onClose: () => void }): JSX.Element {
  const canOpenOrder = useHasAnyPermission('orders.create', 'orders.view_all_shops');
  const rows = useQuery({
    queryKey: ['reserved-orders', level.product.id, level.location.id],
    queryFn: () =>
      apiGet<ReservedOrderRow[]>(`/stock/levels/${level.product.id}/${level.location.id}/reserved-orders`),
  });

  const linked = (rows.data ?? []).reduce((s, r) => s + r.qty, 0);
  const unlinked = level.reserved - linked;

  return (
    <Modal
      title={`Reserved — ${level.product.name}`}
      subtitle={`${level.reserved} unit(s) reserved at ${level.location.name}`}
      size="xl"
      onClose={onClose}
    >
      {rows.isLoading ? (
        <div className="text-center text-ink-3 py-8 text-sm">Loading…</div>
      ) : rows.isError ? (
        <div className="text-sm text-negative">{getApiError(rows.error).message}</div>
      ) : (rows.data ?? []).length === 0 ? (
        <EmptyState
          title="No open orders hold this stock"
          description="The reserved figure isn't linked to any open order — likely a historical or manual adjustment."
        />
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
              <th className="px-3 py-2 font-medium">Order</th>
              <th className="px-3 py-2 font-medium">Customer</th>
              <th className="px-3 py-2 font-medium text-right">Qty</th>
              <th className="px-3 py-2 font-medium">Why it's held</th>
              <th className="px-3 py-2 font-medium">Delivery</th>
            </tr>
          </thead>
          <tbody>
            {(rows.data ?? []).map((r, i) => (
              <tr key={`${r.order.id}-${i}`} className="border-b border-rule-2">
                <td className="px-3 py-3">
                  {canOpenOrder ? (
                    <Link to={`/orders/${r.order.id}`} className="kc-mono text-ink hover:text-accent">
                      {r.order.number ?? r.order.id.slice(0, 8)}
                    </Link>
                  ) : (
                    <span className="kc-mono text-ink">{r.order.number ?? r.order.id.slice(0, 8)}</span>
                  )}
                  <div className="mt-1">
                    <StatusPill tone={orderStatusTone(r.order.status)}>{r.order.status}</StatusPill>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="text-ink">
                    {r.order.customer.type === 'company'
                      ? (r.order.customer.companyName ?? r.order.customer.name)
                      : r.order.customer.name}
                  </div>
                  {r.order.customer.type === 'company' && (
                    <div className="text-xs text-ink-3">Contact: {r.order.customer.name}</div>
                  )}
                  <div className="text-xs text-ink-3">{r.order.shop.code}</div>
                </td>
                <td className="px-3 py-3 text-right kc-mono kc-tnum">{r.qty}</td>
                <td className="px-3 py-3">
                  <StatusPill tone={r.source === 'BACKORDER' ? 'warning' : 'info'}>
                    {r.source === 'BACKORDER' ? 'Backorder' : 'Produced'}
                  </StatusPill>
                  <div className="text-xs text-ink-3 mt-1">
                    {r.source === 'BACKORDER' ? 'Waiting on incoming stock' : 'In warehouse awaiting delivery'}
                    {r.heldSince && <> · since {shortDate(r.heldSince)}</>}
                  </div>
                </td>
                <td className="px-3 py-3 text-xs text-ink-2">{shortDate(r.order.deliveryDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {rows.data && (rows.data.length > 0) && unlinked !== 0 && (
        <div className="mt-3 text-xs bg-warning-soft text-warning border border-warning/30 rounded px-3 py-2">
          {unlinked > 0 ? (
            <>
              <span className="font-medium kc-mono">{unlinked}</span> reserved unit(s) aren't linked to any open
              order — likely a historical or manual adjustment.
            </>
          ) : (
            <>
              Open orders hold <span className="font-medium kc-mono">{linked}</span> unit(s) but the counter shows{' '}
              <span className="font-medium kc-mono">{level.reserved}</span> — the level may be stale; refresh the page.
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

function ReceiveStockButton(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [productId, setProductId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [qty, setQty] = useState('1');
  const [note, setNote] = useState('');
  const qc = useQueryClient();
  const products = useQuery({ queryKey: ['products-all'], queryFn: () => apiGet<Product[]>('/products') });
  const locations = useQuery({ queryKey: ['stock-locations'], queryFn: () => apiGet<StockLocation[]>('/stock/locations') });

  // Receiving stock from suppliers always lands at HQ warehouse. Shops get stock
  // via Transfer afterwards. If there are multiple warehouses (multi-company
  // future), let the user pick — but shops are filtered out entirely.
  const warehouses = (locations.data ?? []).filter((l) => l.kind === 'warehouse');
  const effectiveWarehouseId = warehouseId || warehouses[0]?.id || '';
  const effectiveWarehouse = warehouses.find((w) => w.id === effectiveWarehouseId);

  const submit = useMutation({
    mutationFn: () => apiPost('/stock/receive', { productId, locationId: effectiveWarehouseId, qty: Number(qty), note: note || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stock-levels'] });
      setOpen(false);
      setQty('1');
      setNote('');
    },
  });

  if (!open) return <Button variant="secondary" onClick={() => setOpen(true)}>Receive stock</Button>;

  return (
    <Modal title="Receive stock at HQ" onClose={() => setOpen(false)}>
      <p className="text-xs text-ink-3 mb-4">
        Goods from suppliers always land at the HQ warehouse. Use <span className="text-ink-2">Transfer</span> to move stock out to a shop afterwards.
      </p>
      <div className="flex flex-col gap-1 text-sm">
        <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Product</span>
        <SearchSelect
          value={productId}
          onChange={setProductId}
          placeholder="Search a product by name or SKU…"
          options={(products.data ?? []).map((p) => ({ value: p.id, label: p.name, sublabel: p.sku }))}
        />
      </div>
      <div className="mt-3">
        <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Destination</span>
        {warehouses.length <= 1 ? (
          <div className="mt-1 px-3 py-2 rounded border border-rule bg-paper-2 text-sm text-ink">
            {effectiveWarehouse?.name ?? <span className="text-negative">No warehouse configured — ask an admin</span>}
          </div>
        ) : (
          <select value={effectiveWarehouseId} onChange={(e) => setWarehouseId(e.target.value)} className="kc-input mt-1">
            {warehouses.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        )}
      </div>
      <Input label="Quantity" type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-3" />
      <Input label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} placeholder="PO ref, invoice no., supplier name…" className="mt-3" />
      {submit.isError && <div className="text-xs text-negative mt-2">{getApiError(submit.error).message}</div>}
      <div className="flex gap-2 mt-4">
        <Button variant="secondary" className="flex-1" onClick={() => setOpen(false)}>Cancel</Button>
        <Button className="flex-1" loading={submit.isPending} onClick={() => submit.mutate()} disabled={!productId || !effectiveWarehouseId}>
          Receive at HQ
        </Button>
      </div>
    </Modal>
  );
}

function TransferStockButton(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [productId, setProductId] = useState('');
  const [fromLocationId, setFromLocationId] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [qty, setQty] = useState('1');
  const qc = useQueryClient();
  const products = useQuery({ queryKey: ['products-all'], queryFn: () => apiGet<Product[]>('/products') });
  const locations = useQuery({ queryKey: ['stock-locations'], queryFn: () => apiGet<StockLocation[]>('/stock/locations') });

  // Available stock at the chosen source — used to cap the qty so we never ask
  // to transfer more than the location holds. Backend enforces this too.
  const fromLevels = useQuery({
    queryKey: ['stock-levels', { locationId: fromLocationId }],
    queryFn: () => apiGet<StockLevel[]>('/stock/levels', { locationId: fromLocationId }),
    enabled: !!fromLocationId,
  });
  const available =
    productId && fromLocationId
      ? (() => {
          const lv = (fromLevels.data ?? []).find((l) => l.product.id === productId);
          return lv ? Math.max(0, lv.onHand - lv.reserved - lv.display) : 0;
        })()
      : null;
  const overAvailable = available != null && Number(qty) > available;

  const submit = useMutation({
    mutationFn: () => apiPost('/stock-transfers', { productId, fromLocationId, toLocationId, qty: Number(qty) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfers'] });
      qc.invalidateQueries({ queryKey: ['stock-levels'] });
      setOpen(false);
    },
  });

  if (!open) return <Button onClick={() => setOpen(true)}>+ Transfer</Button>;

  return (
    <Modal title="New stock transfer" onClose={() => setOpen(false)}>
      <div className="flex flex-col gap-1 text-sm">
        <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Product</span>
        <SearchSelect
          value={productId}
          onChange={setProductId}
          placeholder="Search a product by name or SKU…"
          options={(products.data ?? []).map((p) => ({ value: p.id, label: p.name, sublabel: p.sku }))}
        />
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="flex flex-col gap-1 text-sm">
          <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">From</span>
          <SearchSelect
            value={fromLocationId}
            onChange={setFromLocationId}
            placeholder="Search…"
            options={(locations.data ?? []).map((l) => ({ value: l.id, label: l.name }))}
          />
        </div>
        <div className="flex flex-col gap-1 text-sm">
          <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">To</span>
          <SearchSelect
            value={toLocationId}
            onChange={setToLocationId}
            placeholder="Search…"
            options={(locations.data ?? []).map((l) => ({ value: l.id, label: l.name }))}
          />
        </div>
      </div>
      <Input label="Quantity" type="number" min="1" max={available ?? undefined} value={qty} onChange={(e) => setQty(e.target.value)} className="mt-3" />
      {available != null && (
        <div className={`text-xs mt-1 ${overAvailable ? 'text-negative' : 'text-ink-3'}`}>
          {available === 0
            ? 'No stock available at the source location.'
            : overAvailable
              ? `Only ${available} available at the source — reduce the quantity.`
              : `${available} available at the source location.`}
        </div>
      )}
      {submit.isError && <div className="text-xs text-negative mt-2">{getApiError(submit.error).message}</div>}
      <div className="flex gap-2 mt-4">
        <Button variant="secondary" className="flex-1" onClick={() => setOpen(false)}>Cancel</Button>
        <Button className="flex-1" loading={submit.isPending} onClick={() => submit.mutate()} disabled={!productId || !fromLocationId || !toLocationId || fromLocationId === toLocationId || Number(qty) < 1 || overAvailable || available === 0}>
          Create transfer
        </Button>
      </div>
    </Modal>
  );
}

interface Transfer {
  id: string;
  status: 'REQUESTED' | 'IN_TRANSIT' | 'RECEIVED' | 'CANCELLED';
  qty: number;
  receivedQty: number | null;
  requestedAt: string;
  dispatchedAt: string | null;
  receivedAt: string | null;
  note: string | null;
  product: { id: string; sku: string; name: string };
  fromLocation: { id: string; name: string };
  toLocation: { id: string; name: string };
  requestedBy: { id: string; name: string };
}

function TransfersList(): JSX.Element {
  const qc = useQueryClient();
  const transfers = useQuery({ queryKey: ['transfers'], queryFn: () => apiGet<Transfer[]>('/stock-transfers') });

  const action = useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: 'dispatch' | 'receive' | 'cancel' }) =>
      apiPost(`/stock-transfers/${id}/${kind}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfers'] });
      qc.invalidateQueries({ queryKey: ['stock-levels'] });
    },
  });

  return (
    <Card title="Stock transfers" subtitle="Recent transfers across locations" bodyClassName="p-0">
      {transfers.isLoading ? (
        <div className="text-center text-ink-3 px-6 py-8">Loading…</div>
      ) : (transfers.data ?? []).length === 0 ? (
        <EmptyState title="No transfers yet" />
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
              <th className="px-6 py-2 font-medium">Product</th>
              <th className="px-6 py-2 font-medium">From → To</th>
              <th className="px-6 py-2 font-medium text-right">Qty</th>
              <th className="px-6 py-2 font-medium">Status</th>
              <th className="px-6 py-2 font-medium">When</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(transfers.data ?? []).map((t) => (
              <tr key={t.id} className="border-b border-rule-2">
                <td className="px-6 py-3">
                  <div className="text-ink">{t.product.name}</div>
                  <div className="text-xs text-ink-3 kc-mono">{t.product.sku}</div>
                </td>
                <td className="px-6 py-3 text-ink-2 text-xs">
                  {t.fromLocation.name} → {t.toLocation.name}
                </td>
                <td className="px-6 py-3 text-right kc-mono">{t.qty}</td>
                <td className="px-6 py-3"><StatusPill tone={t.status === 'RECEIVED' ? 'positive' : t.status === 'CANCELLED' ? 'negative' : 'info'}>{t.status}</StatusPill></td>
                <td className="px-6 py-3 text-xs text-ink-3">{new Date(t.requestedAt).toLocaleString()}</td>
                <td className="px-6 py-3">
                  <div className="flex gap-1">
                    {t.status === 'REQUESTED' && (
                      <Button size="sm" variant="secondary" onClick={() => action.mutate({ id: t.id, kind: 'dispatch' })}>
                        Dispatch
                      </Button>
                    )}
                    {t.status === 'IN_TRANSIT' && (
                      <Button size="sm" onClick={() => action.mutate({ id: t.id, kind: 'receive' })}>
                        Receive
                      </Button>
                    )}
                    {(t.status === 'REQUESTED' || t.status === 'IN_TRANSIT') && (
                      <Button size="sm" variant="ghost" onClick={() => action.mutate({ id: t.id, kind: 'cancel' })}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// IncomingTransfersCard — sales-girl view: confirm goods that arrived from HQ.
// Lists all IN_TRANSIT transfers TO her shop's stock location, plus a recent-
// history strip of what she's already confirmed (with any shortages flagged).
// ─────────────────────────────────────────────────────────────────────────────

function IncomingTransfersCard(): JSX.Element {
  const transfers = useQuery({
    queryKey: ['transfers-incoming'],
    queryFn: () => apiGet<Transfer[]>('/stock-transfers'),
    refetchInterval: 60_000,
  });
  const [confirming, setConfirming] = useState<Transfer | null>(null);

  const allTransfers = transfers.data ?? [];
  const incoming = allTransfers.filter((t) => t.status === 'IN_TRANSIT');
  // Show the last 5 RECEIVED with discrepancies so the sales girl can see
  // outstanding issues she's flagged.
  const recentDiscrepancies = allTransfers
    .filter((t) => t.status === 'RECEIVED' && t.receivedQty != null && t.receivedQty < t.qty)
    .slice(0, 5);

  return (
    <>
      <Card
        title="Incoming from HQ"
        subtitle="Goods on the truck — confirm when they arrive at your shop"
      >
        {transfers.isLoading ? (
          <div className="text-center text-ink-3 py-8 text-sm">Loading…</div>
        ) : incoming.length === 0 ? (
          <EmptyState
            title="Nothing on the way right now"
            description="When HQ dispatches a transfer to your shop, it'll show up here for you to confirm."
          />
        ) : (
          <ul className="divide-y divide-rule-2">
            {incoming.map((t) => (
              <li key={t.id} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-ink">
                    <span className="kc-mono font-medium">{t.qty}×</span>{' '}
                    {t.product.name}{' '}
                    <span className="text-xs text-ink-3 kc-mono">{t.product.sku}</span>
                  </div>
                  <div className="text-xs text-ink-3 mt-0.5">
                    From <span className="text-ink-2">{t.fromLocation.name}</span>
                    {t.dispatchedAt && (
                      <> · dispatched {new Date(t.dispatchedAt).toLocaleDateString('en-MU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</>
                    )}
                  </div>
                </div>
                <Button size="sm" onClick={() => setConfirming(t)}>
                  Confirm receipt
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {recentDiscrepancies.length > 0 && (
        <Card
          title="Recent shortages reported"
          subtitle="HQ has been notified. Follow up if it's still outstanding."
          bodyClassName="p-0"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                <th className="px-6 py-2 font-medium">Product</th>
                <th className="px-6 py-2 font-medium text-right">Expected</th>
                <th className="px-6 py-2 font-medium text-right">Received</th>
                <th className="px-6 py-2 font-medium text-right">Short</th>
                <th className="px-6 py-2 font-medium">Your note</th>
              </tr>
            </thead>
            <tbody>
              {recentDiscrepancies.map((t) => (
                <tr key={t.id} className="border-b border-rule-2">
                  <td className="px-6 py-3">
                    <div className="text-ink">{t.product.name}</div>
                    <div className="text-xs text-ink-3 kc-mono">{t.product.sku}</div>
                  </td>
                  <td className="px-6 py-3 text-right kc-mono">{t.qty}</td>
                  <td className="px-6 py-3 text-right kc-mono">{t.receivedQty ?? '—'}</td>
                  <td className="px-6 py-3 text-right kc-mono text-negative">
                    {t.qty - (t.receivedQty ?? 0)}
                  </td>
                  <td className="px-6 py-3 text-xs text-ink-3 italic">{t.note ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {confirming && (
        <ConfirmReceiptModal
          transfer={confirming}
          onClose={() => setConfirming(null)}
        />
      )}
    </>
  );
}

function ConfirmReceiptModal({
  transfer,
  onClose,
}: {
  transfer: Transfer;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [receivedQty, setReceivedQty] = useState<string>(String(transfer.qty));
  const [note, setNote] = useState<string>('');
  const submit = useMutation({
    mutationFn: () =>
      apiPost(`/stock-transfers/${transfer.id}/receive`, {
        receivedQty: Number(receivedQty),
        note: note || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transfers-incoming'] });
      qc.invalidateQueries({ queryKey: ['stock-levels'] });
      onClose();
    },
  });

  const expected = transfer.qty;
  const got = Number(receivedQty || 0);
  const shortage = Math.max(0, expected - got);
  const needNote = shortage > 0 || got === 0;
  const canSubmit = got >= 0 && got <= expected && (!needNote || note.trim().length > 0);

  return (
    <Modal title="Confirm receipt" size="md" onClose={onClose}>
      <p className="text-xs text-ink-3 mb-4">
        Verify the quantity that actually arrived at your shop. If something's missing or damaged,
        enter what you received and add a note — HQ will see the discrepancy.
      </p>
      <div className="rounded-md bg-paper-2 p-3 mb-4">
        <div className="text-xs uppercase tracking-wide text-ink-3 mb-1">From HQ</div>
        <div className="text-ink">
          <span className="kc-mono font-medium">{expected}×</span> {transfer.product.name}
        </div>
        <div className="text-xs text-ink-3 kc-mono mt-0.5">{transfer.product.sku}</div>
        <div className="text-xs text-ink-3 mt-2">{transfer.fromLocation.name}</div>
      </div>

      <Input
        label={`Quantity received (expected ${expected})`}
        type="number"
        min={0}
        max={expected}
        step={1}
        value={receivedQty}
        onChange={(e) => setReceivedQty(e.target.value)}
        autoFocus
      />

      {shortage > 0 && (
        <div className="mt-2 text-xs bg-warning-soft text-warning border border-warning/30 rounded px-3 py-2">
          ⚠ Short by <span className="font-medium kc-mono">{shortage}</span>. Please add a note so HQ can investigate.
        </div>
      )}

      <Input
        label={needNote ? 'Note (required for shortages)' : 'Note (optional)'}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="e.g. 1 pillow torn, 2 missing from box"
        className="mt-3"
      />

      {submit.isError && (
        <div className="text-xs text-negative mt-3">{getApiError(submit.error).message}</div>
      )}

      <div className="flex gap-2 mt-5">
        <Button variant="secondary" className="flex-1" onClick={onClose}>Cancel</Button>
        <Button
          className="flex-1"
          loading={submit.isPending}
          disabled={!canSubmit}
          onClick={() => submit.mutate()}
        >
          {shortage > 0 ? 'Confirm with shortage' : 'Confirm all received'}
        </Button>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AddDisplayUnitButton — put a piece on a shop's showroom floor. Two intake
// paths: onboarding a piece the system never held (default — the usual case
// when first registering existing floor pieces), or moving one out of the
// shop's sellable stock.
// ─────────────────────────────────────────────────────────────────────────────

function AddDisplayUnitButton(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [productId, setProductId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [fromStock, setFromStock] = useState(false);
  const [condition, setCondition] = useState('');
  const [note, setNote] = useState('');
  const qc = useQueryClient();
  const products = useQuery({ queryKey: ['products-all'], queryFn: () => apiGet<Product[]>('/products') });
  const locations = useQuery({ queryKey: ['stock-locations'], queryFn: () => apiGet<StockLocation[]>('/stock/locations') });

  // Display units live on shop floors only — the warehouse has no showroom.
  const shops = (locations.data ?? []).filter((l) => l.kind === 'shop');

  const submit = useMutation({
    mutationFn: () =>
      apiPost('/stock/display', {
        productId,
        locationId,
        fromStock,
        condition: condition || null,
        note: note || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stock-levels'] });
      qc.invalidateQueries({ queryKey: ['display-units'] });
      setOpen(false);
      setProductId('');
      setFromStock(false);
      setCondition('');
      setNote('');
    },
  });

  if (!open) return <Button variant="secondary" onClick={() => setOpen(true)}>+ Display unit</Button>;

  return (
    <Modal title="Put a piece on display" onClose={() => setOpen(false)}>
      <p className="text-xs text-ink-3 mb-4">
        A display piece stays in the books but is <span className="text-ink-2">not sellable</span> until
        it's released back to stock, flagged for a clearance sale, or written off.
      </p>
      <div className="flex flex-col gap-1 text-sm">
        <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Product</span>
        <SearchSelect
          value={productId}
          onChange={setProductId}
          placeholder="Search a product by name or SKU…"
          options={(products.data ?? []).map((p) => ({ value: p.id, label: p.name, sublabel: p.sku }))}
        />
      </div>
      <div className="flex flex-col gap-1 text-sm mt-3">
        <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Shop</span>
        <SearchSelect
          value={locationId}
          onChange={setLocationId}
          placeholder="Search a shop…"
          options={shops.map((l) => ({ value: l.id, label: l.name }))}
        />
      </div>
      <div className="mt-3 flex flex-col gap-2 text-sm">
        <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Where does the piece come from?</span>
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="radio" name="display-source" className="mt-0.5 accent-accent" checked={!fromStock} onChange={() => setFromStock(false)} />
          <span>
            <span className="text-ink">It's already on the floor, not tracked yet</span>
            <span className="block text-xs text-ink-3">Adds 1 to the shop's on-hand and puts it straight on display.</span>
          </span>
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="radio" name="display-source" className="mt-0.5 accent-accent" checked={fromStock} onChange={() => setFromStock(true)} />
          <span>
            <span className="text-ink">Take it out of the shop's sellable stock</span>
            <span className="block text-xs text-ink-3">Available drops by 1; on-hand stays the same.</span>
          </span>
        </label>
      </div>
      <Input label="Condition (optional)" value={condition} onChange={(e) => setCondition(e.target.value)} placeholder="e.g. as new / slight fading on left corner" className="mt-3" />
      <Input label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="mt-3" />
      {submit.isError && <div className="text-xs text-negative mt-2">{getApiError(submit.error).message}</div>}
      <div className="flex gap-2 mt-4">
        <Button variant="secondary" className="flex-1" onClick={() => setOpen(false)}>Cancel</Button>
        <Button className="flex-1" loading={submit.isPending} onClick={() => submit.mutate()} disabled={!productId || !locationId}>
          Put on display
        </Button>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DisplayUnitsModal — click a Display figure to see the pieces behind it: how
// long each has been on the floor, its condition, and (permission-gated) the
// actions: flag for a clearance sale, release back to stock, write off.
// ─────────────────────────────────────────────────────────────────────────────

function displayAgeDays(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

function DisplayUnitsModal({ level, onClose }: { level: StockLevel; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const canManage = useHasPermission('stock.display.manage');
  const canPrice = useHasPermission('stock.display.price');
  const units = useQuery({
    queryKey: ['display-units', level.product.id, level.location.id],
    queryFn: () =>
      apiGet<DisplayUnit[]>('/stock/display', { productId: level.product.id, locationId: level.location.id }),
  });

  // One inline action panel at a time: write-off wants a reason, flag-for-sale
  // wants a price — everything else is a straight confirm.
  const [panel, setPanel] = useState<{ unitId: string; mode: 'writeoff' | 'flag' } | null>(null);
  const [reason, setReason] = useState('');
  const [price, setPrice] = useState('');

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['display-units'] });
    void qc.invalidateQueries({ queryKey: ['stock-levels'] });
  };
  const act = useMutation({
    mutationFn: ({ unitId, path, body }: { unitId: string; path: string; body?: unknown }) =>
      apiPost(`/stock/display/${unitId}/${path}`, body),
    onSuccess: () => {
      setPanel(null);
      setReason('');
      setPrice('');
      refresh();
    },
  });

  const open = units.data ?? [];

  return (
    <Modal
      title={`On display — ${level.product.name}`}
      subtitle={`${level.display} piece(s) on the floor at ${level.location.name}`}
      size="xl"
      onClose={onClose}
    >
      {units.isLoading ? (
        <div className="text-center text-ink-3 py-8 text-sm">Loading…</div>
      ) : units.isError ? (
        <div className="text-sm text-negative">{getApiError(units.error).message}</div>
      ) : open.length === 0 ? (
        <EmptyState
          title="No display pieces recorded here"
          description="The display counter isn't backed by any open unit — likely a historical adjustment."
        />
      ) : (
        <ul className="divide-y divide-rule-2">
          {open.map((u) => {
            const age = displayAgeDays(u.assignedAt);
            const stocked = u.product.category.fulfillmentStrategy === 'STOCK_AT_LOCATION';
            const isPanel = panel?.unitId === u.id;
            return (
              <li key={u.id} className="py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <StatusPill tone={u.status === 'FOR_SALE' ? 'warning' : 'info'}>
                        {u.status === 'FOR_SALE' ? 'For sale' : 'On display'}
                      </StatusPill>
                      {u.status === 'FOR_SALE' && u.clearancePrice && (
                        <span className="text-sm text-ink font-medium kc-tnum">{formatMUR(u.clearancePrice)}</span>
                      )}
                    </div>
                    <div className="text-xs text-ink-3 mt-1">
                      On the floor {age === 0 ? 'since today' : `for ${age} day${age === 1 ? '' : 's'}`} · put up by{' '}
                      {u.assignedBy.name}
                    </div>
                    {u.condition && <div className="text-xs text-ink-2 mt-0.5 italic">{u.condition}</div>}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {canPrice && u.status === 'ON_DISPLAY' && (
                      <Button size="sm" variant="secondary" onClick={() => { setPanel({ unitId: u.id, mode: 'flag' }); setPrice(''); }}>
                        Flag for sale
                      </Button>
                    )}
                    {canPrice && u.status === 'FOR_SALE' && (
                      <Button size="sm" variant="ghost" onClick={() => act.mutate({ unitId: u.id, path: 'unflag' })}>
                        Unflag
                      </Button>
                    )}
                    {canManage && stocked && (
                      <Button size="sm" variant="ghost" onClick={() => act.mutate({ unitId: u.id, path: 'release' })}>
                        Back to stock
                      </Button>
                    )}
                    {canManage && (
                      <Button size="sm" variant="ghost" onClick={() => { setPanel({ unitId: u.id, mode: 'writeoff' }); setReason(''); }}>
                        Write off
                      </Button>
                    )}
                  </div>
                </div>

                {isPanel && panel.mode === 'flag' && (
                  <div className="mt-2 flex items-end gap-2 rounded bg-paper-2 p-3">
                    <Input
                      label="Clearance price (incl. VAT)"
                      type="number"
                      min="1"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder={`List ${formatMUR(u.product.salePrice ?? u.product.basePrice)}`}
                      className="flex-1"
                      autoFocus
                    />
                    <Button size="sm" loading={act.isPending} disabled={!(Number(price) > 0)} onClick={() => act.mutate({ unitId: u.id, path: 'flag-for-sale', body: { clearancePrice: Number(price) } })}>
                      Set price
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setPanel(null)}>Cancel</Button>
                  </div>
                )}
                {isPanel && panel.mode === 'writeoff' && (
                  <div className="mt-2 flex items-end gap-2 rounded bg-paper-2 p-3">
                    <Input
                      label="Why is it written off?"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="e.g. torn fabric, sun damage — beyond sale"
                      className="flex-1"
                      autoFocus
                    />
                    <Button size="sm" loading={act.isPending} disabled={!reason.trim()} onClick={() => act.mutate({ unitId: u.id, path: 'write-off', body: { reason: reason.trim() } })}>
                      Write off
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setPanel(null)}>Cancel</Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {act.isError && <div className="text-xs text-negative mt-3">{getApiError(act.error).message}</div>}

      {units.data && open.length > 0 && level.display !== open.length && (
        <div className="mt-3 text-xs bg-warning-soft text-warning border border-warning/30 rounded px-3 py-2">
          The counter shows <span className="font-medium kc-mono">{level.display}</span> but{' '}
          <span className="font-medium kc-mono">{open.length}</span> open piece(s) are recorded — the level may be
          stale; refresh the page.
        </div>
      )}
    </Modal>
  );
}
